import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Sheet, SheetContent, SheetDescription, SheetTitle, } from '../../ui/sheet';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UI_NAMESPACE } from '../../../i18n';
import { findFirstField } from '../../../lib/focus';
import { cn } from '../../../lib/utils';
import { SheetTitleSlotContext } from './sheet-title-slot';
/** Nei form della SheetView anche i combobox sono campi. */
const SHEET_EXTRA_FIELDS = ['[role="combobox"]:not(:disabled)'];
function resolveInitialFocusTarget(initialFocus, content) {
    if (initialFocus === 'first-focusable')
        return null;
    if (!(content instanceof HTMLElement))
        return null;
    if (initialFocus === 'first-field') {
        return findFirstField(content, SHEET_EXTRA_FIELDS) ?? content;
    }
    return content;
}
/**
 * Le sole larghezze ammesse per la SheetView, in px: nessun call-site passa
 * una misura propria.
 *
 * Scala del Previxio DS (min 480 / max 960):
 * - `compact` (560): form brevi a una colonna.
 * - `default` (720): misura standard di form e dettagli.
 * - `wide` (920): pannelli con tabelle o due colonne.
 * - `split` (1040): scheda del movimento (Previxio DS · Conti 6a/6b): la
 *   tabella delle proposte e delle fatture riconciliate non sta a 920.
 *
 * LEGACY, fuori scala: `x-wide` (1200) e `xx-wide` (1280) sono misure
 * ereditate, da non usare per pannelli nuovi. La convergenza su un preset
 * della scala è rinviata perché cambierebbe la larghezza a video.
 */
export const SHEET_SIZES = {
    compact: 560,
    default: 720,
    wide: 920,
    split: 1040,
    'x-wide': 1200,
    'xx-wide': 1280,
};
/**
 * Classi per il `SheetContent` in funzione del layout.
 *
 * Tutti e tre i layout esprimono la posizione esclusivamente tramite `inset`
 * (top/right/bottom/left) — niente `transform`. Questo è il prerequisito per
 * poter animare con CSS transitions il passaggio fra i tre stati: CSS può
 * interpolare fra valori `inset` concreti, ma non fra `translate(-50%,-50%)`
 * e `none`.
 *
 * Per il layout `window` la centratura è ottenuta con `inset` calcolato:
 *   - asse X: `max(1rem, calc(50vw - <size>/2))` → metà della misura del preset
 *   - asse Y: `max(1rem, calc(50vh - 26rem))` → metà della max-height 52rem
 * Il `max(1rem, …)` collassa a 1rem su viewport stretti, equivalente al
 * vecchio `w-[calc(100vw-2rem)]`.
 */
function layoutClassName(layout) {
    // `!w-auto !h-auto` su tutti i layout serve a sovrascrivere `w-3/4 h-full`
    // del componente Sheet base (shadcn/ui), così la larghezza/altezza viene
    // calcolata dal browser in base ai valori `inset` (top/right/bottom/left).
    // `!max-w-none` sovrascrive `sm:max-w-sm` (24rem) di default sempre del
    // Sheet base, che altrimenti clampa la sheet a 384px.
    const baseSize = '!w-auto !h-auto !max-w-none';
    if (layout === 'fullscreen') {
        return `!inset-4 ${baseSize} !rounded-xl !border !overflow-hidden`;
    }
    if (layout === 'window') {
        // left/right dipendono da `size` → inline style (vedi layoutStyle):
        // Tailwind non genera classi costruite a runtime.
        return `!top-[max(1rem,calc(50vh-26rem))] !bottom-[max(1rem,calc(50vh-26rem))] ${baseSize} !rounded-xl !border !overflow-hidden`;
    }
    // side-panel: aderisce al bordo destro/superiore/inferiore della viewport.
    // Il bordo sinistro (larghezza di `size`, mai oltre la viewport) è in
    // layoutStyle.
    return `!inset-y-0 !right-0 ${baseSize} !rounded-none !border-r-0 !border-l !overflow-hidden`;
}
/**
 * Bordo sinistro del pannello laterale: larghezza del preset, mai oltre la
 * viewport. Condiviso con `SheetErrorFallback`, che apre un pannello della
 * stessa misura.
 */
export function sidePanelStyle(size) {
    return { left: `max(0px, calc(100vw - ${SHEET_SIZES[size]}px))` };
}
/** Inset orizzontali che dipendono dalla larghezza del preset. */
function layoutStyle(layout, size) {
    const width = SHEET_SIZES[size];
    if (layout === 'window') {
        const side = `max(1rem, calc(50vw - ${width / 2}px))`;
        return { left: side, right: side };
    }
    if (layout === 'side-panel')
        return sidePanelStyle(size);
    return undefined;
}
/**
 * Manages the rendered content of the sheet.
 *
 * Supporta 3 layout (window / fullscreen / side-panel) tramite il prop
 * `layout`. Il default `side-panel` mantiene il comportamento storico per
 * tutti i form esistenti (nessuna regressione).
 */
export function SheetView({ children, onSheetClose, hasTitle = false, title, description, open = true, hideCloseButton = false, layout = 'side-panel', size = 'default', flushBody = false, initialFocus = 'first-focusable', initialFocusRef, className, }) {
    const { t } = useTranslation(UI_NAMESPACE);
    const [titleSlotCount, setTitleSlotCount] = useState(0);
    const registerTitle = useCallback(() => {
        setTitleSlotCount((count) => count + 1);
        return () => setTitleSlotCount((count) => count - 1);
    }, []);
    // Il titolo visibile della testata fa da nome solo se il chiamante non ne
    // ha dato uno (`title`) né rende il proprio `SheetTitle` (`hasTitle`).
    const titleSlot = !hasTitle && title === undefined ? registerTitle : null;
    const showFallbackTitle = !hasTitle && (title !== undefined || titleSlotCount === 0);
    const handleOpenAutoFocus = (event) => {
        const target = initialFocusRef?.current ??
            resolveInitialFocusTarget(initialFocus, event.target);
        if (!target)
            return;
        event.preventDefault();
        target.focus({ preventScroll: true });
    };
    return (_jsx(Sheet, { open: open, onOpenChange: onSheetClose, children: _jsxs(SheetContent, { className: cn('flex flex-col', 
            // Transizione fluida fra i 3 layout (window/fullscreen/side-panel).
            // Animiamo solo le proprietà che cambiano fra i layout — `transition-all`
            // sarebbe troppo costoso su un `position:fixed` con molti figli.
            'transition-[top,right,bottom,left,max-width,border-radius] duration-300 ease-out motion-reduce:transition-none', layoutClassName(layout), flushBody && 'gap-0', className), style: layoutStyle(layout, size), disableOpenAnimation: true, hideCloseButton: hideCloseButton, onOpenAutoFocus: handleOpenAutoFocus, describedBy: description ? 'sheet-description' : 'none', children: [showFallbackTitle && (_jsx(SheetTitle, { className: "sr-only", children: title ?? t('sheet.title') })), description && (_jsx(SheetDescription, { className: "sr-only", children: description })), _jsx(SheetTitleSlotContext.Provider, { value: titleSlot, children: children })] }) }));
}
export { SheetViewError, } from './components/sheet-view-error';
//# sourceMappingURL=index.js.map